import { useState, useEffect, useCallback } from 'react';
import { getEmployees } from '@/services/employee.service';
import { getDepartments } from '@/services/department.service';

import { useAuthStore } from '@/stores/authStore';

export interface EmployeeStats {
  totalEmployees: number;
  activeEmployees: number;
  onLeaveEmployees: number;
  newEmployeesThisMonth: number;
  totalDepartments: number;
  averageAttendance: number;
}

export interface EmployeeData {
  stats: EmployeeStats;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export const useEmployeeData = (): EmployeeData => {
  const { hasPermission } = useAuthStore();
  const [stats, setStats] = useState<EmployeeStats>({
    totalEmployees: 0,
    activeEmployees: 0,
    onLeaveEmployees: 0,
    newEmployeesThisMonth: 0,
    totalDepartments: 0,
    averageAttendance: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchEmployeeData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const canViewEmployees = hasPermission('EMPLOYEES_VIEW');
      const canViewDepartments = hasPermission('DEPARTMENT_VIEW');

      // Fetch all data in parallel
      const [employeesResponse, departmentsResponse] = await Promise.all([
        canViewEmployees ? getEmployees().catch(err => {
          console.error('Employees API error:', err);
          return { data: [] };
        }) : Promise.resolve({ data: [] }),
        canViewDepartments ? getDepartments().catch(err => {
          console.error('Departments API error:', err);
          return { data: [] };
        }) : Promise.resolve({ data: [] }),
      ]);

      // Process employees data
      let employeesData: any[] = [];
      if (employeesResponse && typeof employeesResponse === 'object') {
        if (Array.isArray(employeesResponse.data)) {
          employeesData = employeesResponse.data;
        } else if (Array.isArray(employeesResponse)) {
          employeesData = employeesResponse;
        }
      }

      // Filter active employees
      const activeEmployees = employeesData.filter((emp: any) => {
        if (!emp || emp.isActive === false) return false;

        const empStatus = typeof emp.status === 'string'
          ? emp.status.toLowerCase()
          : emp.status?.name?.toLowerCase() || '';

        return empStatus === 'active' || empStatus === 'employed';
      });

      // Count employees on leave
      const onLeaveEmployees = employeesData.filter((emp: any) => {
        if (!emp || emp.isActive === false) return false;

        const empStatus = typeof emp.status === 'string'
          ? emp.status.toLowerCase()
          : emp.status?.name?.toLowerCase() || '';

        return empStatus === 'on_leave' || empStatus === 'leave' || empStatus === 'sick_leave';
      }).length;

      // Calculate new employees this month
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const newEmployeesThisMonth = employeesData.filter((emp: any) => {
        if (!emp.joinDate && !emp.joiningDate) return false;
        const joinDate = new Date(emp.joinDate || emp.joiningDate || '');
        return joinDate.getMonth() === currentMonth && joinDate.getFullYear() === currentYear;
      }).length;

      // Process departments data
      let departmentsData: any[] = [];
      if (departmentsResponse && typeof departmentsResponse === 'object') {
        if (Array.isArray(departmentsResponse.data)) {
          departmentsData = departmentsResponse.data;
        } else if (Array.isArray(departmentsResponse)) {
          departmentsData = departmentsResponse;
        }
      }

      // Filter active departments
      const activeDepartments = departmentsData.filter((dept: any) => {
        return dept && dept.isActive !== false;
      });

      // Calculate average attendance (mock calculation for now)
      const averageAttendance = activeEmployees.length > 0 ? 95 : 0; // This would come from attendance API

      // Update stats
      const newStats: EmployeeStats = {
        totalEmployees: employeesData.length,
        activeEmployees: activeEmployees.length,
        onLeaveEmployees,
        newEmployeesThisMonth,
        totalDepartments: activeDepartments.length,
        averageAttendance,
      };

      setStats(newStats);
      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching employee data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch employee data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployeeData();
  }, [fetchEmployeeData]);

  return {
    stats,
    loading,
    error,
    lastUpdated,
  };
}; 